import { MerchFlowCoreRange, MerchFlowFlowHierarchy } from "@CommonApi/merchandise";
import { useEffect, useMemo, useState } from "react";
import { useAtom } from "jotai";
import { StepsNavigator } from "src/components";
import { Flex, Modal } from "src/elements";
import { uuid } from "src/utils";
import { Step1Details } from "./components/Step1Details";
import { MerchflowReviewSelectedFilters, MerchflowReviewStep } from "./store/types";
import { ResponseGetMerchflowsCategories } from "@CommonApi/merchflows/categories";
import { useMerchflowsApi } from "src/api/merchflows";
import { ResponsePostMerchflowsUhd } from "@CommonApi/merchflows/uhd";
import { Step2Filters } from "./components/Step2Filters";
import { ResponseGetMerchflowsFilters } from "@CommonApi/merchflows/filters";
import { ResponsePutMerchflows_merchflowId_Filters } from "@CommonApi/merchflows/_reviewId_/filters";
import { ResponseGetMerchflowsVariants } from "@CommonApi/merchflows/variants";
import { Step3MergeRules } from "./components/Step3MergeRules";
import { Step2StoresUhd } from "./components/Step2StoresUhd";
import { PayloadPutMerchflows_merchflowId_FilterConfigs_filterConfigId_MergeRulesV2 } from "@CommonApi/merchflows/_merchflowId_/filter-configs/_filterConfig_id/merge-rules-v2";
import { simulationContextAtom } from "src/pages/AssortmentSimulation/store/atoms";
import { matchCategoryKey } from "src/pages/AssortmentSimulation/store/hooks";
import {
  cdtVersionContextAtom,
  pendingGenerationCdtSourceAtom,
} from "src/pages/CDTTreeEditor/store/cdtVersionContextAtom";
import { useSetAtom } from "jotai";

/**
 * Finds the merchflow category / sub-category pair for a CDT category: its
 * code is a sub-category (category_code) of some retailer category, or, failing
 * that, a retailer category itself.
 */
export const matchCdtCategory = (
  availableCategories: ResponseGetMerchflowsCategories,
  categoryCode?: string,
  categoryGroup?: string,
): { category: string; subcategory: string | null } | null => {
  if (!categoryCode) return null;
  for (const [category, subcategories] of Object.entries(availableCategories)) {
    const subcategory = matchCategoryKey(categoryCode, subcategories);
    if (subcategory) return { category, subcategory };
  }
  const category = matchCategoryKey(categoryCode, Object.keys(availableCategories));
  if (category) return { category, subcategory: null };
  const group = categoryGroup && matchCategoryKey(categoryGroup, Object.keys(availableCategories));
  return group ? { category: group, subcategory: null } : null;
};

export const ModalMerchflowReview = () => {
  const { getMerchflowsCategoriesApi, getMerchflowsFiltersApi } = useMerchflowsApi();
  const [simulationContext, setSimulationContext] = useAtom(simulationContextAtom);
  const [cdtVersionContext, setCdtVersionContext] = useAtom(cdtVersionContextAtom);
  const setPendingGenerationCdtSource = useSetAtom(pendingGenerationCdtSourceAtom);

  const [step, setStep] = useState<MerchflowReviewStep>("STEP_1_DETAILS");

  // UHD FLOW DATA. A flow started from a CDT version is a UHD flow without a PSA.
  const [generationMode, setGenerationMode] = useState<"UHD" | "LD" | null>(
    cdtVersionContext ? "UHD" : null,
  );
  const [psaFile, setPsaFile] = useState<File | null>(null);
  const [uhdFlowResponse, setUhdFlowResponse] = useState<ResponsePostMerchflowsUhd | null>(null);

  // DETAILS DATA.
  const [merchflowId, setMerchflowId] = useState<number | null>(null);
  const [name, setName] = useState(simulationContext?.name ?? uuid());
  const [notes, setNotes] = useState("");
  const [goLiveDate, setGoLiveDate] = useState<Date | null>(new Date());
  const [deactivationDate, setDeactivationDate] = useState<Date | null>(null);
  const [coreRange, setCoreRange] = useState<MerchFlowCoreRange>(MerchFlowCoreRange.PARTIAL);
  const [category, setCategory] = useState<string | null>(null);
  const [subcategory, setSubcategory] = useState<string | null>(null);
  const [availableCategories, setAvailableCategories] =
    useState<ResponseGetMerchflowsCategories | null>(null);

  // FILTERS DATA.
  const [availableFilters, setAvailableFilters] = useState<ResponseGetMerchflowsFilters | null>(
    null,
  );
  const [selectedFilters, setSelecterFilters] = useState<MerchflowReviewSelectedFilters>({
    baySizes: [],
    states: [],
    fixtureHeights: [],
    fixtureWidths: [],
    clusters: [],
  });
  const [appliedFilters, setAppliedFilters] =
    useState<ResponsePutMerchflows_merchflowId_Filters | null>(null);

  // MERGE RULES DATA.
  const [availableVariants, setAvailableVariants] = useState<ResponseGetMerchflowsVariants | null>(
    null,
  );
  const [mergeRules, setMergeRules] = useState<string[][]>([]);
  const [flowHierarchy, setFlowHierarchy] = useState<MerchFlowFlowHierarchy[]>([
    MerchFlowFlowHierarchy.CORE_RANGE,
    MerchFlowFlowHierarchy.DOS,
    MerchFlowFlowHierarchy.MOS,
  ]);
  const [operationMode, setOperationMode] =
    useState<
      PayloadPutMerchflows_merchflowId_FilterConfigs_filterConfigId_MergeRulesV2["operation_mode"]
    >("MODELLED");

  const modalTitle = `New Flow ${category ? `- ${category}` : ""} ${
    subcategory ? ` / ${subcategory}` : ""
  } ${name ? `- ${name}` : ""}`;

  useEffect(() => {
    loadCategories();
    return () => {
      setSimulationContext(null);
      // Cancelling leaves nothing behind; a flow that proceeded has already
      // handed what the Generate dialog needs to pendingGenerationCdtSourceAtom.
      setCdtVersionContext(null);
    };
  }, []);

  // Pre-fill category from simulation context once categories load.
  useEffect(() => {
    if (!simulationContext || !availableCategories) return;
    const matched = matchCategoryKey(
      simulationContext.categoryCode,
      Object.keys(availableCategories),
    );
    if (matched) setCategory(matched);
  }, [simulationContext, availableCategories]);

  // The category of the CDT version the flow was started from. It is fixed:
  // the preselected stores and their version labels belong to it.
  const cdtCategory = useMemo(
    () =>
      cdtVersionContext && availableCategories
        ? matchCdtCategory(
            availableCategories,
            cdtVersionContext.categoryCode,
            cdtVersionContext.categoryGroup,
          )
        : null,
    [cdtVersionContext, availableCategories],
  );

  // Pre-fill it.
  useEffect(() => {
    if (!cdtCategory) return;
    setCategory(cdtCategory.category);
    if (cdtCategory.subcategory) setSubcategory(cdtCategory.subcategory);
  }, [cdtCategory]);

  // Pre-fill cluster filter from simulation context once filters load.
  useEffect(() => {
    if (!simulationContext || !availableFilters) return;
    if (simulationContext.geoLevel !== "Cluster" || !simulationContext.geoDetail) return;

    const matchedCluster = availableFilters.clusters.find(
      (c) => c.toLowerCase() === simulationContext.geoDetail!.toLowerCase(),
    );

    if (matchedCluster) {
      setSelecterFilters((prev) => ({
        ...prev,
        clusters: [matchedCluster],
      }));
    }
  }, [simulationContext, availableFilters]);

  // Generate merge rule from each and every variant once after variants are loaded in.
  useEffect(() => {
    if (availableVariants) {
      setMergeRules(availableVariants.map((variant) => [variant]));
    }
  }, [availableVariants]);

  const loadCategories = async () => {
    setAvailableCategories(await getMerchflowsCategoriesApi());
  };

  const onUhdFlowCreated = async (response: ResponsePostMerchflowsUhd) => {
    setUhdFlowResponse(response);
    setStep("STEP_2_STORES_UHD");
    const filters = await getMerchflowsFiltersApi({
      retailerCategoryCode: response.retailer_category_code,
      categoryCode: response.category_code,
    });
    setAvailableFilters(filters);
  };

  const isUhd = generationMode === "UHD";
  const steps = [
    {
      title: "Flow Details",
      isActive: step === "STEP_1_DETAILS",
      onClick: () => setStep("STEP_1_DETAILS"),
    },
    {
      title: "Stores Selection",
      isActive: step === (isUhd ? "STEP_2_STORES_UHD" : "STEP_2_FILTERS"),
      onClick: () => setStep(isUhd ? "STEP_2_STORES_UHD" : "STEP_2_FILTERS"),
    },
    ...(!isUhd
      ? [
          {
            title: "Merge Rules",
            isActive: step === "STEP_3_MERGE_RULES",
            onClick: () => setStep("STEP_3_MERGE_RULES"),
          },
        ]
      : []),
  ];

  return (
    <Modal name="ModalMerchflowReview" width="1200px" title={modalTitle}>
      <Flex minHeight="70vh" padding="20px" column gap="40px">
        <StepsNavigator steps={steps} />

        <Flex flexGrow>
          {step === "STEP_1_DETAILS" && (
            <Step1Details
              setStep={setStep}
              merchflowId={merchflowId}
              setMerchflowId={setMerchflowId}
              name={name}
              setName={setName}
              notes={notes}
              setNotes={setNotes}
              goLiveDate={goLiveDate}
              setGoLiveDate={setGoLiveDate}
              deactivationDate={deactivationDate}
              setDeactivationDate={setDeactivationDate}
              coreRange={coreRange}
              setCoreRange={setCoreRange}
              category={category}
              setCategory={setCategory}
              subcategory={subcategory}
              setSubcategory={setSubcategory}
              availableCategories={availableCategories}
              setAvailableFilters={setAvailableFilters}
              simulationId={simulationContext?.simulationId}
              cdtVersionContext={cdtVersionContext}
              lockedCategory={cdtCategory}
              generationMode={generationMode}
              setGenerationMode={setGenerationMode}
              psaFile={psaFile}
              setPsaFile={setPsaFile}
              onUhdFlowCreated={onUhdFlowCreated}
            />
          )}

          {step === "STEP_2_STORES_UHD" && uhdFlowResponse && (
            <Step2StoresUhd
              uhdFlowResponse={uhdFlowResponse}
              availableFilters={availableFilters}
              cdtVersionContext={cdtVersionContext}
              onProceed={() => {
                if (!cdtVersionContext) return;
                setPendingGenerationCdtSource({
                  merchflowId: uhdFlowResponse.id,
                  source: "CATEGORY_TREE",
                });
              }}
            />
          )}

          {step === "STEP_2_FILTERS" && merchflowId && (
            <Step2Filters
              merchflowId={merchflowId}
              availableFilters={availableFilters}
              setStep={setStep}
              selectedFilters={selectedFilters}
              setSelectedFilters={setSelecterFilters}
              appliedFilters={appliedFilters}
              setAppliedFilters={setAppliedFilters}
              setAvailableVariants={setAvailableVariants}
            />
          )}

          {step === "STEP_3_MERGE_RULES" && appliedFilters && (
            <Step3MergeRules
              merchflowId={appliedFilters.review_id}
              filterConfigId={appliedFilters.id}
              setStep={setStep}
              availableVariants={availableVariants}
              mergeRules={mergeRules}
              setMergeRules={setMergeRules}
              flowHierarchy={flowHierarchy}
              setFlowHierarchy={setFlowHierarchy}
              operationMode={operationMode}
              setOperationMode={setOperationMode}
            />
          )}
        </Flex>
      </Flex>
    </Modal>
  );
};
